/**
 * nova-expressions.ts — canonical expression catalog (22 avatar expressions).
 * The 8 persona tiles (cowgirl, chef, DJ…) are standalone components in
 * ./personas/. `id` doubles as the CSS wrapper class: `expr-<id>`.
 * Never hand-tune opts — every platform reads the same catalog.
 */
import { NOVA_COLORS as C } from "./nova-palette";
import type { NovaExpressionDef } from "./nova-types";

export const NOVA_EXPRESSION_IDS = [
  "idle", "curious", "thinking", "suggestion", "happy", "sleepy",
  "celebrating", "camera", "sad", "cross", "shocked", "storage",
  "love", "laughing", "dizzy", "wink", "starstruck", "smug",
  "pleading", "suspicious", "loading", "crying-laughing",
] as const;

export type NovaExpressionId = (typeof NOVA_EXPRESSION_IDS)[number];

export const NOVA_EXPRESSIONS: Record<NovaExpressionId, NovaExpressionDef> = {
  idle: {
    id: "idle", label: "Idle", timing: "3s float · 4s blink",
    caption: "Default. Triangles up, soft signal blue.",
    opts: { eyeShape: "triUp", eyeColor: C.blue },
  },
  curious: {
    id: "curious", label: "Curious", timing: "2s tilt · 4s blink",
    caption: "Eyes tilt inward. Body leans forward.",
    opts: { eyeShape: "triUp", eyeColor: C.blue, eyeAsym: true },
  },
  thinking: {
    id: "thinking", label: "Thinking", timing: "3s build cycle",
    caption: "Triangles built block-by-block. Antenna ring spinning.",
    opts: { eyeShape: "thinkBlocks", eyeColor: C.orange, antennaSpin: true },
  },
  suggestion: {
    id: "suggestion", label: "Suggesting", timing: "1.2s bounce · 4s blink",
    caption: "Bright eyes. Antenna ball glowing.",
    opts: { eyeShape: "triUp", eyeColor: C.blue, eyeBright: "bright", cheeks: true, antennaGlow: true },
  },
  happy: {
    id: "happy", label: "Happy", timing: "2s sway · cheek pulse",
    caption: "Smiling-eye curves. Cheeks blushing.",
    opts: { eyeShape: "curveSmile", eyeColor: C.blue, cheeks: true },
  },
  sleepy: {
    id: "sleepy", label: "Sleepy", timing: "5s drift",
    caption: "Flat eyes. Antenna drooping.",
    opts: { eyeShape: "flat", eyeColor: C.blue, sleepy: true },
  },
  celebrating: {
    id: "celebrating", label: "Celebrating", timing: "0.8s bounce · confetti",
    caption: "Arms up. Confetti rains down.",
    opts: { eyeShape: "triUp", eyeColor: C.blue, eyeBright: "bright", cheeks: true, arms: "celebrate", confetti: true, antennaGlow: true },
  },
  camera: {
    id: "camera", label: "Camera", timing: "4s hover · 5s click",
    caption: "Holding a vintage film camera.",
    opts: { eyeShape: "triUpForward", eyeColor: C.blue, arms: "camera" },
  },
  sad: {
    id: "sad", label: "Sad", timing: "5s drift · 4s tear · 5s blink",
    caption: "A quiet melancholy. With you, not at you.",
    opts: {
      eyeShape: "teardrop", eyeColor: C.blue, cheeks: true, cheekColor: C.coral, cheekOpacity: 0.4,
      antennaTilt: -25, bodyGapShrink: 8, tilt: 5, tear: true,
    },
  },
  cross: {
    id: "cross", label: "Cross", timing: "0.3s tension · 3s huff · 2s antenna heat",
    caption: "Frustrated with you, not at you.",
    opts: {
      eyeShape: "triFurrow", eyeColor: C.blue, cheeks: true, cheekColor: "#E89478", cheekOpacity: 0.6,
      antennaTilt: -8, antennaBallColor: C.orange, tilt: -8, stressMark: true,
    },
  },
  shocked: {
    id: "shocked", label: "Shocked", timing: "3.5s recoil · 0.4s antenna shake",
    caption: "Wide-eyed and pulled back. Did NOT see that coming.",
    opts: {
      eyeShape: "shocked", eyeColor: C.blue, cheeks: true, cheekColor: C.coral, cheekOpacity: 0.4,
      antennaTilt: 12, tilt: 4,
    },
  },
  storage: {
    id: "storage", label: "Storage", timing: "6s door cycle",
    caption: "Chest door swings open. She keeps a star and a spare camera in there.",
    opts: { eyeShape: "curveSmile", eyeColor: C.blue, cheeks: true },
  },
  love: {
    id: "love", label: "Love", timing: "1s heartbeat · cheek glow",
    caption: "Heart eyes, beating in sync. Utterly smitten.",
    opts: { eyeShape: "hearts", eyeColor: "#E86A8D", cheeks: true, cheekColor: C.coral, cheekOpacity: 0.65, tilt: 2 },
  },
  laughing: {
    id: "laughing", label: "Laughing", timing: "0.4s giggle shake · 3s wave",
    caption: "Eyes squeezed shut, whole body shaking with giggles.",
    opts: { eyeShape: "laugh", eyeColor: C.blue, cheeks: true, cheekOpacity: 0.6, tilt: -3 },
  },
  dizzy: {
    id: "dizzy", label: "Dizzy", timing: "1.6s spiral spin · 2.4s wobble",
    caption: "Spiral eyes spinning. Too many orbits at once.",
    opts: { eyeShape: "dizzy", eyeColor: C.blue, antennaTilt: 16, tilt: -5 },
  },
  wink: {
    id: "wink", label: "Wink", timing: "4s wink · antenna flick",
    caption: "One eye closes into a smile. She knows something you don't.",
    opts: { eyeShape: "wink", eyeColor: C.blue, cheeks: true, tilt: -2 },
  },
  starstruck: {
    id: "starstruck", label: "Starstruck", timing: "1.8s twinkle · glow pulse",
    caption: "Star eyes twinkling. Completely dazzled.",
    opts: { eyeShape: "stars", eyeColor: C.mustard, cheeks: true, antennaGlow: true, tilt: 3 },
  },
  smug: {
    id: "smug", label: "Smug", timing: "3s slow sway · 5s eyebrow",
    caption: "Half-lidded and very pleased with herself.",
    opts: { eyeShape: "smug", eyeColor: C.blue, cheeks: true, tilt: -3 },
  },
  pleading: {
    id: "pleading", label: "Pleading", timing: "2.2s shimmer · wobble",
    caption: "Puppy eyes at maximum. Physically impossible to refuse.",
    opts: { eyeShape: "pleading", eyeColor: C.blue, cheeks: true, cheekOpacity: 0.55, antennaTilt: -14, tilt: 4, bodyGapShrink: 4 },
  },
  suspicious: {
    id: "suspicious", label: "Suspicious", timing: "4s pupil sweep · lean",
    caption: "Narrowed slits. Something doesn't add up.",
    opts: { eyeShape: "suspicious", eyeColor: C.blue, tilt: -6 },
  },
  loading: {
    id: "loading", label: "Loading", timing: "1.2s dot pulse · ring spin",
    caption: "Buffering… three dots per eye. Please hold.",
    opts: { eyeShape: "loading", eyeColor: C.blue, antennaSpin: true },
  },
  "crying-laughing": {
    id: "crying-laughing", label: "Crying-laughing", timing: "0.5s shake · 3s tear arc",
    caption: "Laughing so hard the tears fly sideways.",
    opts: { eyeShape: "crying", eyeColor: C.blue, cheeks: true, cheekOpacity: 0.65, tilt: 5, tear: true },
  },
};
